<template>
  <div
    v-if="teaserSize"
    class="marketing-teaser"
    :class="[
      `teaser-type__${teaserSize}`,
      { 'is-image-only': isImageOnly, 'is-loading': isLoading }
    ]"
  >
    <template v-if="isLoading">
      <loader />
    </template>

    <template v-else>
      <template v-if="teaserMultiTypes.includes(teaser.type)">
        <div
          class="o-grid o-grid--small-full o-grid--medium-full o-grid--large-full"
        >
          <div
            v-for="count in multiTeaserCount"
            :key="count"
            class="o-grid__cell"
          >
            <div
              class="teaser-wrapper"
              :data-count="count"
              :class="{ 'is-hover': teaserHoverStates[count - 1] }"
              @mouseover="toggleHoverState(count, true)"
              @mouseleave="toggleHoverState(count, false)"
              @click.capture="handleClick(count)"
            >
              <div class="teaser-image">
                <a
                  class="image-link"
                  :target="getTarget(getProperty('ctaUrl', count))"
                  :href="getProperty('ctaUrl', count)"
                  :style="{
                    'background-image': `url(${getTeaserImageAsBase64(count)})`
                  }"
                  ref="teaserLink"
                ></a>
              </div>
              <div class="teaser-content">
                <div
                  class="content-inner"
                  v-if="
                    getProperty('title', count) ||
                      getProperty('text', count) ||
                      getProperty('ctaText', count)
                  "
                >
                  <h3>{{ getProperty('title', count) }}</h3>
                  <p>{{ getProperty('text', count) }}</p>
                  <bos-button
                    v-if="getProperty('ctaText', count)"
                    class="btn-white btn-small"
                    :target="getTarget(getProperty('ctaUrl', count))"
                    :class="{ 'is-hover': teaserHoverStates[count - 1] }"
                    :href="getProperty('ctaUrl', count)"
                    >{{ getProperty('ctaText', count) }}</bos-button
                  >
                </div>

                <div
                  class="content-eyecatcher"
                  v-if="
                    getProperty('eyecatcherTitle', count) ||
                      getProperty('eyecatcherText', count)
                  "
                >
                  <div class="eyecatcher-inner">
                    <div class="eyecatcher-title">
                      {{ getProperty('eyecatcherTitle', count) }}
                    </div>
                    <div class="eyecatcher-text">
                      {{ getProperty('eyecatcherText', count) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div
          class="teaser-wrapper"
          :class="{ 'is-hover': teaserHoverStates[0] }"
          @mouseover="toggleHoverState(1, true)"
          @mouseleave="toggleHoverState(1, false)"
          @click.capture="handleClick()"
        >
          <div class="teaser-image">
            <a
              class="image-link"
              :target="getTarget(teaser.ctaUrl)"
              :href="teaser.ctaUrl"
              :style="{ 'background-image': `url(${getTeaserImageAsBase64()})` }"
              ref="teaserLink"
            ></a>
          </div>
          <div class="teaser-content">
            <div
              class="content-inner"
              v-if="teaser.title || teaser.text || teaser.ctaText"
            >
              <h3>{{ teaser.title }}</h3>
              <p>{{ teaser.text }}</p>
              <bos-button
                v-if="teaser.ctaText"
                class="btn-white"
                :target="getTarget(teaser.ctaUrl)"
                :class="{ 'is-hover': teaserHoverStates[0] }"
                :href="teaser.ctaUrl"
                >{{ teaser.ctaText }}</bos-button
              >
            </div>

            <div
              class="content-eyecatcher"
              v-if="teaser.eyecatcherTitle || teaser.eyecatcherText"
            >
              <div class="eyecatcher-inner">
                <div class="eyecatcher-title">{{ teaser.eyecatcherTitle }}</div>
                <div class="eyecatcher-text">{{ teaser.eyecatcherText }}</div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ApiService from '@/services/ApiService'
import { getLocalizationInExcludes, localize } from '@/assets/js/helper'
import Loader from '@/components/BosLoader'
import BosButton from '@/components/BosButton'
import LinkService from '@/services/LinkService'

export default {
  name: 'MarketingTeaser',
  props: {
    teaserType: {
      type: Number
    },
    fair: {
      type: String,
      default: ''
    },
    boothId: {
      type: String,
      default: ''
    },
    tasksDone: {
      type: Array,
      default: () => [-1]
    },
    boothState: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      tasksDoneCheck: [-1],
      isLoading: false,
      teaserTypes: [],
      // TODO: It would be nice if the info came from the backend
      teaserMultiTypes: [4, 132],
      // TODO: It would be nice if the info came from the backend
      teaserSizes: {
        xl: [1, 129],
        md: [2, 130],
        sm: [4, 132, 136]
      },
      teaserData: {},
      multiTeaserCount: 3,
      teaserHoverStates: [],
      requestUuid: ''
    }
  },
  computed: {
    ...mapGetters(['oidcUser']),
    teaserTypeToBeRequested() {
      return (
        this.teaserTypes.find(type => type.value === this.teaserType) || {}
      ).value
    },
    teaserSize() {
      return Object.keys(this.teaserSizes).find(size =>
        this.teaserSizes[size].includes(this.teaserData.type)
      )
    },
    teaser() {
      let inAndExcludes = getLocalizationInExcludes(this.$i18n.locale)
      return localize(
        this.teaserData,
        inAndExcludes.include,
        inAndExcludes.exclude
      )
    },
    imageOrientation() {
      return this.$mq === 'xlg' || this.$mq === 'lg' ? 'Landscape' : 'Portrait'
    },
    isImageOnly() {
      return (this.teaser.type & 128) > 0
    }
  },
  methods: {
    getTarget(link) {
      return LinkService.getTarget(link)
    },
    getTeaserTypes() {
      this.isLoading = true

      ApiService.getTeaserTypes().then(response => {
        this.teaserTypes = response.data || []
        this.getTeaserOfType()
      })
    },
    getTeaserOfType() {
      this.isLoading = true
      this.teaserData = {}

      if (this.teaserTypeToBeRequested) {
        this.requestUuid = Math.random()
          .toString(36)
          .substr(2)
        ApiService.getTeaserOfType(
          this.teaserTypeToBeRequested,
          this.oidcUser,
          this.fair,
          this.boothId,
          this.tasksDone,
          this.boothState,
          this.requestUuid
        )
          .then(response => {
            if (response.data.uuid === this.requestUuid) {
              this.teaserData = response.data.teaser || {}
            }
          })
          .finally(() => {
            this.isLoading = false
            this.$emit('loaded')
          })
      } else {
        console.warn(
          `Can't load Teaser, because type ${
            this.teaserType
          } is not existing! Available types are: ${this.teaserTypes.map(
            type => type.value
          )}.`
        )
      }
    },
    getProperty(baseName = '', count = 1) {
      return this.teaser[baseName + (count > 1 ? count : '')]
    },
    getTeaserImageAsBase64(count = 1) {
      let image = this.getTeaserImage(count);
      if (image.type && image.type === 'Buffer') {
        let img = '';
        for (let char in image.data) {
          img += String.fromCharCode(image.data[char]);
        }
        return img;
      }
      return image;
    },
    getTeaserImage(count = 1) {
      return this.teaser[
        'image' + this.imageOrientation + (count > 1 ? count : '')
      ]
    },
    toggleHoverState(count = 1, state = false) {
      this.$set(this.teaserHoverStates, [count - 1], state === true)
    },
    handleClick(count = 1) {
      let links = [].concat(this.$refs.teaserLink)
      links[count - 1].click()
    }
  },
  watch: {
    teaserType() {
      this.getTeaserOfType()
    },
    tasksDone() {
      let check = JSON.parse(JSON.stringify(this.tasksDone))
      let check2 = JSON.parse(JSON.stringify(this.tasksDoneCheck))
      let match = true

      if (check.length !== check2.length) {
        match = false
      } else {
        for (var i = 0; i < check.length; i++) {
          if (check[i] !== check2[i]) {
            match = false
            break
          }
        }
      }

      if (!match) {
        this.tasksDoneCheck = check
        this.getTeaserOfType()
      }
    },
    boothState() {
      this.getTeaserOfType()
    }
  },
  created() {
    while (this.multiTeaserCount - this.teaserHoverStates.length > 0) {
      this.teaserHoverStates.push(false)
    }

    this.getTeaserTypes()
  },
  components: {
    Loader,
    BosButton
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/mixins/teaser.scss';

$teaser-margin: 4.5rem 0 !default;
$teaser-loading-min-height: 10em !default;
$teaser-grid-gutter: $grid-gutter !default;

$teaser-image-link-transition: all 400ms ease-out !default;
$teaser-image-link-hover-scale: 1.05 !default;

$teaser-content-bg: $brand-gold !default;
$teaser-content-title-margin: 0 0 0.5em !default;
$teaser-content-inner-max-width: 500px !default;
$teaser-content-title-font-weight: 300 !default;
$teaser-content-title-color: $brand-white !default;
$teaser-content-text-margin: 0 0 1em !default;
$teaser-content-text-color: $teaser-content-title-color !default;
$teaser-content-button-margin: 0 !default;

$teaser-eyecatcher-bg: $brand-red !default;
$teaser-eyecatcher-rotation: -12.5deg !default;
$teaser-eyecatcher-title-font-weight: 600 !default;
$teaser-eyecatcher-title-color: $brand-white !default;
$teaser-eyecatcher-text-font-weight: $teaser-eyecatcher-title-font-weight !default;
$teaser-eyecatcher-text-color: $teaser-eyecatcher-title-color !default;

$teaserVariantXL: (
  'size': xl,
  'ratio': calc( 3/2 ),
  'ratio-image-only': calc( 3/1 ),
  'image-width': 50%,
  'content-padding': 2em,
  'content-font-size': inherit,
  'content-title-font-size': 2.25em,
  'eyecatcher-position-y': top,
  'eyecatcher-position-x': right,
  'eyecatcher-size': 8.5vw,
  'eyecatcher-title-font-size': 2vw,
  'eyecatcher-text-font-size': 1.1vw,
  'ratio-mobile': calc( 5/3 ),
  'ratio-mobile-image-only': calc( 5/6 ),
  'content-mobile-padding': 2em 20px,
  'content-mobile-font-size': 1rem,
  'content-mobile-title-font-size': 2em,
  'eyecatcher-mobile-position-y': top,
  'eyecatcher-mobile-position-x': right,
  'eyecatcher-mobile-size': 14vw,
  'eyecatcher-mobile-title-font-size': 3vw,
  'eyecatcher-mobile-text-font-size': 2vw
) !default;

$teaserVariantMD: (
  'size': md,
  'ratio': calc( 1/1 ),
  'ratio-image-only': calc( 10/3 ),
  'image-width': 30%,
  'content-padding': 1.5em,
  'content-font-size': inherit,
  'content-title-font-size': 1.5em,
  'eyecatcher-position-y': top,
  'eyecatcher-position-x': left,
  'eyecatcher-size': 5.25em,
  'eyecatcher-title-font-size': 1.4em,
  'eyecatcher-text-font-size': 0.8em,
  'ratio-mobile': calc( 5/3 ),
  'ratio-mobile-image-only': calc( 5/6 ),
  'content-mobile-padding': 1.5em 20px,
  'content-mobile-font-size': 1rem,
  'content-mobile-title-font-size': 1.5em,
  'eyecatcher-mobile-position-y': top,
  'eyecatcher-mobile-position-x': right,
  'eyecatcher-mobile-size': 14vw,
  'eyecatcher-mobile-title-font-size': 4vw,
  'eyecatcher-mobile-text-font-size': 2.4vw
) !default;

$teaserVariantSM: (
  'size': sm,
  'ratio': calc( 10/7 ),
  'ratio-image-only': calc( 5/6 ),
  'image-width': 100%,
  'content-padding': 1.25em,
  'content-font-size': 1rem,
  'content-title-font-size': 1.5em,
  'eyecatcher-position-y': top,
  'eyecatcher-position-x': left,
  'eyecatcher-size': 4em,
  'eyecatcher-title-font-size': 1.35em,
  'eyecatcher-text-font-size': 0.6em,
  'ratio-mobile': calc( 5/3 ),
  'ratio-mobile-image-only': calc( 5/6 ),
  'content-mobile-padding': 1.5em 20px,
  'content-mobile-font-size': 0.8rem,
  'content-mobile-title-font-size': 1.5em,
  'eyecatcher-mobile-position-y': top,
  'eyecatcher-mobile-position-x': right,
  'eyecatcher-mobile-size': 14vw,
  'eyecatcher-mobile-title-font-size': 4vw,
  'eyecatcher-mobile-text-font-size': 2.4vw
) !default;

.marketing-teaser {
  position: relative;
  margin: $teaser-margin;
  overflow: hidden;

  .o-grid {
    margin: $teaser-grid-gutter * -0.5;

    .o-grid__cell {
      padding: $teaser-grid-gutter * 0.5;
    }
  }

  .teaser-wrapper {
    display: flex;
    flex-wrap: wrap;
    justify-content: stretch;
    align-content: stretch;
    align-items: stretch;
    position: relative;
    cursor: pointer;

    .teaser-image {
      overflow: hidden;
      min-height: 100%;

      .image-link {
        display: block;
        position: relative;
        height: 100%;
        background: no-repeat center center;
        background-size: cover;
        transition: $teaser-image-link-transition;
      }
    }

    .teaser-content {
      flex: 1 0 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: $teaser-content-bg;
      text-align: center;

      .content-inner {
        max-width: $teaser-content-inner-max-width;

        h3 {
          margin: $teaser-content-title-margin;
          font-weight: $teaser-content-title-font-weight;
          color: $teaser-content-title-color;
        }

        p {
          margin: $teaser-content-text-margin;
          color: $teaser-content-text-color;
        }

        .btn {
          margin: $teaser-content-button-margin;
        }

        *:last-child {
          margin-bottom: 0;
        }
      }

      .content-eyecatcher {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        overflow: hidden;
        margin: 0.5em;
        background-color: $teaser-eyecatcher-bg;
        text-align: center;
        line-height: 1;
        border-radius: 50%;
        transform: rotate($teaser-eyecatcher-rotation);

        .eyecatcher-inner {
          .eyecatcher-title {
            font-weight: $teaser-eyecatcher-title-font-weight;
            color: $teaser-eyecatcher-title-color;
          }

          .eyecatcher-text {
            font-weight: $teaser-eyecatcher-text-font-weight;
            color: $teaser-eyecatcher-text-color;
          }
        }
      }

      &:after {
        display: block;
        content: '';
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        height: 1000px;
        background-color: inherit;
      }
    }

    &.is-hover {
      .teaser-image {
        .image-link {
          transform: scale($teaser-image-link-hover-scale);
        }
      }
    }
  }

  &.is-loading {
    height: $teaser-loading-min-height;
  }

  .md &,
  .sm &,
  .xs & {
    .teaser-wrapper {
      .teaser-content {
        &:after {
          display: none;
        }
      }
    }
  }

  @include teaser($teaserVariantXL);
  @include teaser($teaserVariantMD);
  @include teaser($teaserVariantSM);
}
</style>
